import * as THREE from "three";
import type { AmbientCritterDef, MapData } from "@dogdell/shared";
import { Tile, tileAt } from "@dogdell/shared";
import { critterSprite } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraYaw, facingFlipSign, SPRITE_FOOT_Y } from "./WorldArt";
import type { DogPose } from "./ParkCritter";

type FamilyMode = "parade" | "scatter" | "gather" | "settle";

const FLEE_RADIUS = 5.2;
const SAFE_RADIUS = 7.2;
const SLOT_SPACING = 0.72;
const MOM_WANDER = 1.35;
const MOM_FLEE = 2.55;
const BABY_WANDER = 1.55;
const BABY_FLEE = 3.1;
const BABY_GATHER = 2.4;

interface DuckMember {
  group: THREE.Group;
  mesh: THREE.Mesh;
  spriteH: number;
  x: number;
  y: number;
  facing: number;
  seed: number;
  /** Flee heading while scattered. */
  fleeAng: number;
}

/**
 * Mother duck + a line of ducklings.
 * Dogs scare them apart; mom always gathers the babies back into a tidy row.
 */
export class ParkDuckFamily {
  readonly root = new THREE.Group();
  private readonly mother: DuckMember;
  private readonly babies: DuckMember[] = [];
  private mode: FamilyMode = "parade";
  private modeUntil = 0;
  private goalX: number;
  private goalY: number;
  private paradeFacing = 0;
  private readonly roam: number;
  private readonly homeX: number;
  private readonly homeY: number;
  private stuck = 0;

  constructor(
    private readonly map: MapData,
    def: AmbientCritterDef,
    private readonly groundY: (x: number, y: number) => number,
  ) {
    this.homeX = def.homeX + 0.5;
    this.homeY = def.homeY + 0.5;
    this.goalX = this.homeX;
    this.goalY = this.homeY;
    this.roam = def.roam ?? 10;
    const n = Math.max(3, Math.min(7, def.familySize ?? 5));

    this.mother = this.makeMember("duck", this.homeX, this.homeY, hash(def.id));
    this.root.add(this.mother.group);

    for (let i = 0; i < n; i++) {
      const slot = this.slotBehind(this.homeX, this.homeY, Math.PI, i);
      const baby = this.makeMember("duckling", slot.x, slot.y, hash(`${def.id}-b${i}`));
      this.babies.push(baby);
      this.root.add(baby.group);
    }

    this.paradeFacing = 0;
    this.pickParadeGoal(performance.now());
    this.modeUntil = performance.now() + 800;
  }

  get count(): number {
    return 1 + this.babies.length;
  }

  update(dtSec: number, now: number, camera: THREE.Camera, dogs: DogPose[]): void {
    const threat = this.nearestDog(dogs);
    const dogNear = threat && threat.dist < FLEE_RADIUS;
    const dogSafe = !threat || threat.dist > SAFE_RADIUS;

    if (dogNear && this.mode !== "scatter") {
      this.beginScatter(threat!);
    } else if (this.mode === "scatter" && dogSafe && now >= this.modeUntil) {
      this.beginGather(now);
    }

    if (this.mode === "scatter") {
      this.tickScatter(dtSec, threat);
    } else if (this.mode === "gather") {
      this.tickGather(dtSec, now);
    } else if (this.mode === "settle") {
      this.tickSettle(dtSec, now);
    } else {
      this.tickParade(dtSec, now);
    }

    this.placeMember(this.mother, camera, now, this.mode === "parade" || this.mode === "scatter" || this.mode === "gather");
    for (const baby of this.babies) {
      const moving =
        this.mode === "scatter" ||
        this.mode === "gather" ||
        this.mode === "parade";
      this.placeMember(baby, camera, now, moving);
    }
  }

  destroy(): void {
    this.root.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (!child.geometry.userData.shared) {
          child.geometry.dispose();
        }
        if (child.material instanceof THREE.Material && !child.material.userData.shared) {
          child.material.dispose();
        }
      }
    });
    this.root.removeFromParent();
  }

  private beginScatter(threat: { x: number; y: number }): void {
    this.mode = "scatter";
    this.modeUntil = performance.now() + 1600;
    const base = Math.atan2(this.mother.y - threat.y, this.mother.x - threat.x);
    this.mother.fleeAng = base + (this.mother.seed - 0.5) * 0.6;
    for (let i = 0; i < this.babies.length; i++) {
      const baby = this.babies[i]!;
      // Fan out so the row bursts apart instead of stacking.
      const fan = (i - (this.babies.length - 1) * 0.5) * 0.55;
      baby.fleeAng = base + fan + (baby.seed - 0.5) * 0.9;
    }
  }

  private beginGather(now: number): void {
    this.mode = "gather";
    // Rally near home, slightly toward current center of mass.
    let cx = this.mother.x;
    let cy = this.mother.y;
    for (const baby of this.babies) {
      cx += baby.x;
      cy += baby.y;
    }
    cx /= this.count;
    cy /= this.count;
    const hx = this.homeX;
    const hy = this.homeY;
    this.goalX = hx + (cx - hx) * 0.35;
    this.goalY = hy + (cy - hy) * 0.35;
    if (!this.isShoreOpen(this.goalX, this.goalY)) {
      this.goalX = hx;
      this.goalY = hy;
    }
    this.paradeFacing = Math.atan2(this.goalY - this.mother.y, this.goalX - this.mother.x) || this.paradeFacing;
    this.modeUntil = now + 8000;
  }

  private tickScatter(dtSec: number, threat: { x: number; y: number; dist: number } | null): void {
    this.stepMember(this.mother, Math.cos(this.mother.fleeAng) * MOM_FLEE * dtSec, Math.sin(this.mother.fleeAng) * MOM_FLEE * dtSec, true);
    this.mother.facing = this.mother.fleeAng;
    // Mom steers a little toward the flock center so she can round them up after.
    let bx = 0;
    let by = 0;
    for (const baby of this.babies) {
      bx += baby.x;
      by += baby.y;
    }
    bx /= this.babies.length;
    by /= this.babies.length;
    const toFlock = Math.atan2(by - this.mother.y, bx - this.mother.x);
    this.mother.fleeAng = lerpAngle(this.mother.fleeAng, toFlock, 0.04);

    for (const baby of this.babies) {
      if (threat) {
        const away = Math.atan2(baby.y - threat.y, baby.x - threat.x);
        baby.fleeAng = lerpAngle(baby.fleeAng, away, 0.08);
      }
      this.stepMember(baby, Math.cos(baby.fleeAng) * BABY_FLEE * dtSec, Math.sin(baby.fleeAng) * BABY_FLEE * dtSec, true);
      baby.facing = baby.fleeAng;
    }
  }

  private tickGather(dtSec: number, now: number): void {
    const toGoalX = this.goalX - this.mother.x;
    const toGoalY = this.goalY - this.mother.y;
    const gDist = Math.hypot(toGoalX, toGoalY);
    if (gDist > 0.35) {
      this.paradeFacing = Math.atan2(toGoalY, toGoalX);
      this.mother.facing = this.paradeFacing;
      this.stepMember(this.mother, (toGoalX / gDist) * MOM_WANDER * 1.35 * dtSec, (toGoalY / gDist) * MOM_WANDER * 1.35 * dtSec, false);
    }

    let allHome = gDist < 0.45;
    for (let i = 0; i < this.babies.length; i++) {
      const baby = this.babies[i]!;
      const slot = this.slotBehind(this.mother.x, this.mother.y, this.paradeFacing, i);
      const dx = slot.x - baby.x;
      const dy = slot.y - baby.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 0.22) {
        allHome = false;
        baby.facing = Math.atan2(dy, dx);
        this.stepMember(baby, (dx / dist) * BABY_GATHER * dtSec, (dy / dist) * BABY_GATHER * dtSec, true);
      } else {
        baby.x = slot.x;
        baby.y = slot.y;
        baby.facing = this.paradeFacing;
      }
    }

    if (allHome || now >= this.modeUntil) {
      this.mode = "settle";
      this.modeUntil = now + 1400 + this.mother.seed * 900;
      this.snapBabiesToRow();
    }
  }

  private tickSettle(dtSec: number, now: number): void {
    this.snapBabiesToRow();
    // Soft idle wobble in place.
    this.mother.facing = this.paradeFacing;
    void dtSec;
    if (now >= this.modeUntil) {
      this.mode = "parade";
      this.pickParadeGoal(now);
    }
  }

  private tickParade(dtSec: number, now: number): void {
    const dx = this.goalX - this.mother.x;
    const dy = this.goalY - this.mother.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 0.4) {
      if (now >= this.modeUntil) {
        this.pickParadeGoal(now);
        this.modeUntil = now + 900 + this.mother.seed * 1200;
      }
    } else {
      this.paradeFacing = Math.atan2(dy, dx);
      this.mother.facing = this.paradeFacing;
      const moved = this.stepMember(
        this.mother,
        (dx / dist) * MOM_WANDER * dtSec,
        (dy / dist) * MOM_WANDER * dtSec,
        false,
      );
      if (!moved) {
        this.stuck += 1;
        if (this.stuck >= 3) {
          this.pickParadeGoal(now);
          this.stuck = 0;
        }
      } else {
        this.stuck = 0;
      }
    }

    for (let i = 0; i < this.babies.length; i++) {
      const baby = this.babies[i]!;
      const slot = this.slotBehind(this.mother.x, this.mother.y, this.paradeFacing, i);
      const bx = slot.x - baby.x;
      const by = slot.y - baby.y;
      const bd = Math.hypot(bx, by);
      if (bd > 0.12) {
        baby.facing = Math.atan2(by, bx);
        const speed = Math.min(BABY_WANDER * 1.25, bd * 3.2);
        this.stepMember(baby, (bx / bd) * speed * dtSec, (by / bd) * speed * dtSec, true);
      } else {
        baby.facing = this.paradeFacing;
      }
    }
  }

  private snapBabiesToRow(): void {
    for (let i = 0; i < this.babies.length; i++) {
      const baby = this.babies[i]!;
      const slot = this.slotBehind(this.mother.x, this.mother.y, this.paradeFacing, i);
      baby.x = slot.x;
      baby.y = slot.y;
      baby.facing = this.paradeFacing;
    }
  }

  private slotBehind(x: number, y: number, facing: number, index: number): { x: number; y: number } {
    const back = index + 1;
    return {
      x: x - Math.cos(facing) * SLOT_SPACING * back,
      y: y - Math.sin(facing) * SLOT_SPACING * back,
    };
  }

  private pickParadeGoal(now: number): void {
    for (let attempt = 0; attempt < 12; attempt++) {
      const ang = (this.mother.seed * 19.1 + attempt * 2.513 + now * 0.0007) % (Math.PI * 2);
      const radius = 2.2 + ((this.mother.seed * 11 + attempt * 0.41) % 1) * Math.max(2, this.roam * 0.7);
      const gx = this.homeX + Math.cos(ang) * radius;
      const gy = this.homeY + Math.sin(ang) * radius;
      if (this.isShoreOpen(gx, gy)) {
        this.goalX = gx;
        this.goalY = gy;
        return;
      }
    }
    this.goalX = this.homeX + (this.mother.seed - 0.5) * 2;
    this.goalY = this.homeY + (hash("goal") - 0.5) * 2;
  }

  private stepMember(m: DuckMember, dx: number, dy: number, allowLoose: boolean): boolean {
    const nx = m.x + dx;
    const ny = m.y + dy;
    if (allowLoose) {
      // Scatter / catch-up can leave the roam circle briefly.
      if (!this.isShoreOpen(nx, ny) && !this.isSoftOpen(nx, ny)) {
        return false;
      }
      m.x = clamp(nx, -2, this.map.width + 2);
      m.y = clamp(ny, -2, this.map.height + 2);
      // Soft leash back toward home when very far.
      const distHome = Math.hypot(m.x - this.homeX, m.y - this.homeY);
      if (distHome > this.roam + 6) {
        const pull = (distHome - this.roam - 6) * 0.08;
        m.x += ((this.homeX - m.x) / distHome) * pull;
        m.y += ((this.homeY - m.y) / distHome) * pull;
      }
      return true;
    }
    if (!this.isShoreOpen(nx, ny)) {
      return false;
    }
    if (Math.hypot(nx - this.homeX, ny - this.homeY) > this.roam + 0.8) {
      return false;
    }
    m.x = nx;
    m.y = ny;
    return true;
  }

  /** Preferred shore tiles for calm walking. */
  private isShoreOpen(x: number, y: number): boolean {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (tx < 0 || ty < 0 || tx >= this.map.width || ty >= this.map.height) {
      return false;
    }
    const tile = tileAt(this.map, tx, ty);
    if (
      tile === undefined ||
      tile === Tile.Water ||
      tile === Tile.Rock ||
      tile === Tile.Fence ||
      tile === Tile.Tree ||
      tile === Tile.Bush
    ) {
      return false;
    }
    return true;
  }

  /** Slightly looser for flee bursts (still not into deep water / rocks). */
  private isSoftOpen(x: number, y: number): boolean {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (tx < 0 || ty < 0 || tx >= this.map.width || ty >= this.map.height) {
      return true; // map edge ok while fleeing
    }
    const tile = tileAt(this.map, tx, ty);
    return tile !== Tile.Water && tile !== Tile.Rock && tile !== Tile.Fence;
  }

  private makeMember(kind: "duck" | "duckling", x: number, y: number, seed: number): DuckMember {
    const sheet = critterSprite(kind);
    const group = new THREE.Group();
    const shadow = makeContactShadow({
      radius: Math.min(0.3, sheet.width * 0.34),
      opacity: kind === "duck" ? 0.5 : 0.4,
      y: 0.015,
    });
    group.add(shadow);
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(sheet.width, sheet.height),
      billboardMaterial(sheet.texture),
    );
    mesh.position.y = sheet.height * SPRITE_FOOT_Y;
    mesh.renderOrder = 2;
    group.add(mesh);
    return {
      group,
      mesh,
      spriteH: sheet.height,
      x,
      y,
      facing: 0,
      seed,
      fleeAng: 0,
    };
  }

  private placeMember(m: DuckMember, camera: THREE.Camera, now: number, moving: boolean): void {
    const gy = this.groundY(m.x - 0.5, m.y - 0.5);
    m.group.position.set(m.x, gy, m.y);
    const bob = moving ? Math.abs(Math.sin(now * 0.016 + m.seed * 8)) * 0.03 : Math.sin(now * 0.004 + m.seed) * 0.006;
    const squash = moving ? 1 + Math.sin(now * 0.016 + m.seed) * 0.04 : 1;
    m.mesh.position.y = m.spriteH * SPRITE_FOOT_Y + bob;
    faceCameraYaw(m.mesh, camera);
    const flip = facingFlipSign(m.x, m.y, m.facing, camera);
    m.mesh.scale.set(flip * squash, squash, 1);
  }

  private nearestDog(dogs: DogPose[]): { x: number; y: number; dist: number } | null {
    let best: { x: number; y: number; dist: number } | null = null;
    // Threat measured from the mother (family unit).
    for (const dog of dogs) {
      const dx = dog.x + 0.5 - this.mother.x;
      const dy = dog.y + 0.5 - this.mother.y;
      const dist = Math.hypot(dx, dy);
      if (!best || dist < best.dist) {
        best = { x: dog.x + 0.5, y: dog.y + 0.5, dist };
      }
    }
    return best;
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function hash(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}

function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}
